import {
    Button,
    Variant,
} from '@genomics-dev/denim-components';
import {
    useCallback,
    useMemo,
} from 'react';
import {
    FormattedDate,
    FormattedMessage,
} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {
    FetchError,
    FetchValidationError,
    Loading,
    Test,
} from '../../../components';
import {useAmplitude} from '../../../context';
import {
    useMutation,
    useQuery,
    useTraitEligibility,
} from '../../../hooks';
import {Routes} from '../../../Routes';
import {
    Endpoint,
    extractErrorMessage,
} from '../../../util';

export function YourReports() {
    const amplitude = useAmplitude();

    const {
        error: confirmError,
        isPending: isConfirmPending,
        mutateAsync: confirmRegistration,
    } = useMutation({
        endpoint: Endpoint.CONFIRM_REGISTRATION,
        updateCache: true,
        updateCacheKey: [Endpoint.SUMMARY],
    });
    const navigate = useNavigate();
    const {data: summaryData} = useQuery({endpoint: Endpoint.SUMMARY});
    // Get the test order expiry date and don't use a cached value in case the date of birth or today's date has changed
    const {data: testOrderExpiryData} = useQuery({
        endpoint: Endpoint.TEST_ORDER_EXPIRY,
        staleTime: 0,
    });
    const {
        eligibleTraits,
        error: traitError,
        ineligibleTraits,
        isError: isTraitError,
        isPending: isTraitPending,
    } = useTraitEligibility();

    const onClickHandler = useCallback(async () => {
        await confirmRegistration();
        amplitude.logAnswersConfirmed();

        navigate(Routes.HOME);
    }, [amplitude, summaryData]);

    const expiryDate = useMemo(() => (
        testOrderExpiryData?.expiryDate
            ? (
                <FormattedDate
                    day={'2-digit'}
                    month={'long'}
                    timeZone={'UTC'}
                    value={new Date(testOrderExpiryData.expiryDate)}
                    year={'numeric'}
                />
            )
            : null
    ), [testOrderExpiryData]);

    if (isTraitPending) {
        return <Loading />;
    }

    if (isTraitError) {
        return <FetchError reason={extractErrorMessage(traitError)} />;
    }

    return (
        <>
            <h2 data-test-id={'sign-up-my-reports-title'}>
                <FormattedMessage id={'signUp.myReports.title'} />
            </h2>
            <FetchValidationError errorResponse={confirmError} />
            {Boolean(eligibleTraits?.length) && (
                <div className={'test-block'}>
                    <div className={'is-caption is-heavy'}>
                        <FormattedMessage id={'common.profile.tests.included'} />
                    </div>
                    {eligibleTraits.map(({trait}) => (
                        <Test
                            dataTestId={`included-trait-${trait}`}
                            included={true}
                            key={trait}
                            trait={trait}
                        />
                    ))}
                </div>
            )}
            {Boolean(ineligibleTraits?.length) && (
                <div className={'test-block'}>
                    <div className={'is-caption is-heavy'}>
                        <FormattedMessage id={'common.profile.tests.notIncluded'} />
                    </div>
                    {ineligibleTraits.map(({trait}) => (
                        <Test
                            dataTestId={`not-included-trait-${trait}`}
                            included={false}
                            key={trait}
                            trait={trait}
                        />
                    ))}
                </div>
            )}
            {expiryDate && (
                <p data-test-id={'sign-up-my-reports-expiry-date'}>
                    <FormattedMessage
                        id={'signUp.myReports.expiryDate'}
                        values={{expiryDate}}
                    />
                </p>
            )}
            <Button
                dataTestId={'sign-up-confirm-answers'}
                isLoading={isConfirmPending}
                onClick={onClickHandler}
                variant={Variant.PRIMARY}
            >
                <FormattedMessage id={'signUp.myReports.confirmAnswers.submit'} />
            </Button>
        </>
    );
}
